<template>
  <div class="sites">

    <header class="masthead">
      <h1 class="title">Sites</h1>
    </header>

    <p v-if="loading" class="msg">Reading the directory</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="countries.length === 0" class="msg">
      No countries recorded. Vero reads the usage location and office fields in
      Entra, and neither is set on any account yet.
      <button class="link" @click="load">Check again</button>
    </p>

    <div v-else class="list">
      <section v-for="country in countries" :key="country.code" class="country">

        <div class="head">
          <span class="chip">{{ country.code }}</span>
          <h2 class="name">{{ country.name }}</h2>
          <span class="meta">{{ siteLabel(locationsIn(country).length) }}</span>

          <router-link
            class="go"
            :to="{ name: 'CountryRoster', params: { country: country.code } }"
          >
            All employees
            <span class="go-count">{{ headcount(country) }}</span>
          </router-link>
        </div>

        <!-- Offices as a plain row, not a card each. They carry one number
             between them, and a box the size of a tile to hold it was mostly
             empty space. -->
        <!-- Vero's own locations, not Entra offices. The Entra field held
             whatever anyone had typed into it -- a domain name in one case --
             and none of it matched the locations doors are assigned to. -->
        <ul v-if="locationsIn(country).length > 0" class="offices">
          <li v-for="loc in locationsIn(country)" :key="loc.name" class="office">
            <span class="office-name">{{ loc.name }}</span>
          </li>
        </ul>

        <p v-else class="none">
          No offices yet for {{ country.name }}. Add one under Offices.
        </p>
      </section>
    </div>

    <!-- Coverage. During rollout the number that matters is how much of the
         directory is actually filled in, not how much rendered. -->
    <footer v-if="!loading && !error && stats" class="coverage">
      <!-- "placed" used to mean "has an Entra officeLocation", which no longer
           describes anything: people are not assigned to Vero locations at all,
           only doors are. -->
      <span><b>{{ stats.unassigned }}</b> no country</span>
      <span><b>{{ stats.excluded }}</b> not in Vero People</span>
      <span><b>{{ stats.total }}</b> in directory</span>
    </footer>

  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue'
import { getSiteHierarchy } from '@/services/GraphService'
import { getLocations } from '@/services/OfficeColourService'

const countries = ref([])
const stats = ref(null)
const locations = ref([])
const loading = ref(true)
const error = ref(null)

// Everyone in the country, whatever their Entra officeLocation says. People are
// not assigned to Vero locations -- only doors are -- so this is simply how many
// group members carry that usageLocation, and it matches what the roster shows.
function headcount(country) {
  return country.sites.reduce((sum, s) => sum + (s.headcount ?? 0), 0)
    + (country.peopleWithoutSite ?? 0)
}

function siteLabel(n) {
  if (n === 0) return 'no offices'
  return n === 1 ? '1 office' : `${n} offices`
}

function locationsIn(country) {
  return locations.value.filter((l) => l.country === country.code)
}

async function load() {
  loading.value = true
  error.value = null

  try {
    // Countries and headcounts from Entra; locations from Vero.
    const [result, locationList] = await Promise.all([
      getSiteHierarchy(),
      // Not fatal: without it the chips are empty, which is wrong but does not
      // stop the page showing who is where.
      getLocations().catch(() => [])
    ])

    locations.value = locationList

    // null means MSAL is redirecting for consent; the page is navigating away.
    if (result === null) return

    countries.value = result.countries
    stats.value = result.stats
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>


<style scoped>
.sites {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;

  max-width: 1000px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 8px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

/* ---------- Countries ---------- */

.list {
  display: flex;
  flex-direction: column;
}

.country {
  padding: 28px 0;
  border-bottom: 1px solid var(--line);
}

.country:last-child { border-bottom: none; }

.head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.chip {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: #fff;
  padding: 5px 10px;
  border-radius: 6px;
}

.name {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: var(--ink);
}

.meta {
  font-size: 14px;
  color: var(--ink-soft);
}

.go {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 17px;
  white-space: nowrap;
}

.go:hover {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

.go-count {
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}

/* ---------- Offices ---------- */

/* Wrapping row of small entries. An office is a name and a count; anything
   larger is padding around two facts. */
.offices {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.office {
  display: inline-flex;
  align-items: baseline;
  gap: 12px;
  background: var(--wash);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 16px;
}

.office-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
}


.none {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 10px 0 0;
}

/* ---------- Coverage ---------- */

.coverage {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
  font-size: 14px;
  color: var(--ink-soft);
}

.coverage b {
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  margin-right: 4px;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
  line-height: 1.55;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 15px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 640px) {
  .sites { padding: 0 20px 60px; }
  .head { flex-wrap: wrap; }
  .go { margin-left: 0; }
}
</style>
